'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CONSENT_REOPEN_EVENT,
  noConsent,
  type Purpose,
  PURPOSE_COPY,
  type PurposeAnswers,
} from '@/lib/consent';
import { recordConsent } from '@/lib/consentStats';
import { useConsent } from './useConsent';

/**
 * Asks what the visitor agrees to, purpose by purpose, and takes no for an
 * answer on each of them separately.
 *
 * Three rules shape everything here and all three are legal rather than
 * aesthetic.
 *
 * Refusing has to be exactly as easy as agreeing — so Decline all and Allow all
 * are the same size, the same shape and the same weight, and neither is styled
 * to be the one you reach for. The familiar "by choosing Allow, you agree…"
 * sentence only reads honestly while that second button is there; the
 * single-OK version of it is the pattern regulators actually fine.
 *
 * Consent has to be specific to each purpose, so there is a switch for each,
 * every one of them off until somebody turns it on. A pre-ticked box is not
 * consent, and neither is a single control covering three different things.
 *
 * And nothing may load before an answer — so no tag is merely told to hold
 * back, none is requested at all. See GoogleAnalytics, Hotjar and MetaPixel.
 *
 * It is a bar, not a wall. The page underneath stays readable and usable while
 * the question is open, because conditioning access on consent would make the
 * consent worthless.
 */
export default function CookieConsent({ purposes }: { purposes: Purpose[] }) {
  const { choice, decide } = useConsent();
  /* Reopened from the footer, so an answer can be changed as easily as it was
     given — which the law requires and which is also just decent. */
  const [reopened, setReopened] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<PurposeAnswers>(noConsent());

  useEffect(() => {
    const onReopen = () => {
      /* Reopened to change an answer, so start from the answer on file rather
         than from nothing — otherwise the panel silently proposes withdrawing
         everything. */
      setDraft(choice ? { ...noConsent(), ...pick(choice, purposes) } : noConsent());
      setExpanded(true);
      setReopened(true);
    };
    window.addEventListener(CONSENT_REOPEN_EVENT, onReopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, onReopen);
  }, [choice, purposes]);

  /* `undefined` is "not read yet" and must render nothing: the server does not
     know the answer, so anything else here is a hydration mismatch and a banner
     that flashes at people who already answered. */
  const open = choice === undefined ? false : choice === null || reopened;

  const answer = (answers: PurposeAnswers) => {
    decide(answers);
    setExpanded(false);
    setReopened(false);
    /* Counted after the choice has been stored and acted on, never before, and
       deliberately not awaited: the visitor's answer must take effect whether
       or not a counter somewhere succeeds. Three booleans go to our own
       database and nothing else — no identifier, no address, one row per day.
       See consentStats.ts for why that restraint is the point. */
    void recordConsent(answers);
  };

  const everything = (granted: boolean): PurposeAnswers => {
    const answers = noConsent();
    /* Only what was actually asked about. A purpose with nothing configured is
       never granted by an Allow all, because nobody was shown it. */
    for (const purpose of purposes) answers[purpose] = granted;
    return answers;
  };

  const button: React.CSSProperties = {
    fontSize: 11,
    letterSpacing: '0.14em',
    padding: '11px 20px',
    minHeight: 44,
    border: '1px solid rgba(var(--m-on-ink-rgb),0.28)',
    background: 'transparent',
    color: 'rgba(var(--m-on-ink-rgb),0.9)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease)',
  };

  const hover = (on: boolean) => (event: React.MouseEvent<HTMLButtonElement>) => {
    const element = event.currentTarget;
    element.style.background = on ? 'rgba(var(--m-on-ink-rgb),0.09)' : 'transparent';
    element.style.borderColor = `rgba(var(--m-on-ink-rgb),${on ? 0.5 : 0.28})`;
  };

  const quiet: React.CSSProperties = {
    fontSize: 10,
    letterSpacing: '0.14em',
    color: 'rgba(var(--m-on-ink-rgb),0.55)',
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    minHeight: 32,
    display: 'inline-flex',
    alignItems: 'center',
    background: 'transparent',
    border: 0,
    padding: 0,
    cursor: 'pointer',
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="region"
          aria-label="Cookie consent"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-50"
          style={{
            background: 'var(--m-ink)',
            borderTop: '1px solid rgba(var(--m-on-ink-rgb),0.14)',
            maxHeight: '85vh',
            overflowY: 'auto',
          }}
        >
          <div className="m-container py-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
              <div style={{ maxWidth: 720 }}>
                <p
                  className="font-mono uppercase mb-2"
                  style={{
                    fontSize: 10,
                    letterSpacing: '0.16em',
                    color: 'rgba(var(--m-on-ink-rgb),0.42)',
                  }}
                >
                  Cookies
                </p>
                <p
                  className="text-sm"
                  style={{ color: 'rgba(var(--m-on-ink-rgb),0.72)', lineHeight: 1.65 }}
                >
                  By choosing Allow, you agree to cookies being stored on your device to enhance
                  site navigation, analyse site usage, and improve marketing. You can choose which
                  of those you agree to, and declining changes nothing else — everything here works
                  the same either way.
                </p>

                {/* The links sit outside the sentence rather than inside it.
                    Inline links split a paragraph into fragments, and the
                    translator works on whole text nodes — a sentence with a
                    link buried in it is a sentence that stays English. */}
                <div className="flex flex-wrap items-center gap-5 mt-3">
                  {[
                    { href: '/cookie-policy', label: 'Cookie Policy' },
                    { href: '/privacy-policy', label: 'Privacy Policy' },
                  ].map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      className="font-mono uppercase"
                      style={quiet}
                    >
                      {link.label}
                    </Link>
                  ))}
                  <button
                    type="button"
                    className="font-mono uppercase"
                    style={quiet}
                    aria-expanded={expanded}
                    onClick={() => setExpanded((was) => !was)}
                  >
                    {expanded ? 'Hide choices' : 'Choose individually'}
                  </button>
                </div>
              </div>

              {/* Same size, same shape, same weight. A decline button quieter
                  than the one beside it is not a choice, and is the single most
                  commonly enforced defect in a consent banner. */}
              <div className="flex items-center gap-3 shrink-0">
                <button
                  type="button"
                  className="font-mono uppercase"
                  style={button}
                  onMouseEnter={hover(true)}
                  onMouseLeave={hover(false)}
                  onClick={() => answer(everything(false))}
                >
                  Decline all
                </button>
                <button
                  type="button"
                  className="font-mono uppercase"
                  style={button}
                  onMouseEnter={hover(true)}
                  onMouseLeave={hover(false)}
                  onClick={() => answer(everything(true))}
                >
                  Allow all
                </button>
              </div>
            </div>

            {expanded && (
              <div
                className="mt-6 pt-5"
                style={{ borderTop: '1px solid rgba(var(--m-on-ink-rgb),0.14)' }}
              >
                <div className="flex flex-col gap-4" style={{ maxWidth: 720 }}>
                  {purposes.map((purpose) => (
                    <label
                      key={purpose}
                      className="flex gap-3 items-start"
                      style={{ cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        checked={draft[purpose]}
                        onChange={(event) =>
                          setDraft((was) => ({ ...was, [purpose]: event.target.checked }))
                        }
                        style={{
                          marginTop: 3,
                          width: 16,
                          height: 16,
                          accentColor: 'var(--m-accent)',
                        }}
                      />
                      <span>
                        <span
                          className="font-mono uppercase block"
                          style={{
                            fontSize: 10,
                            letterSpacing: '0.14em',
                            color: 'rgba(var(--m-on-ink-rgb),0.9)',
                          }}
                        >
                          {PURPOSE_COPY[purpose].label}
                        </span>
                        <span
                          className="text-sm block mt-1"
                          style={{ color: 'rgba(var(--m-on-ink-rgb),0.58)', lineHeight: 1.6 }}
                        >
                          {PURPOSE_COPY[purpose].description}
                        </span>
                      </span>
                    </label>
                  ))}

                  <div className="flex items-center gap-3 mt-1">
                    <button
                      type="button"
                      className="font-mono uppercase"
                      style={button}
                      onMouseEnter={hover(true)}
                      onMouseLeave={hover(false)}
                      onClick={() => answer(draft)}
                    >
                      Save choices
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** The answers for the purposes actually on offer, ignoring any others that
 *  may sit in a stored record from a differently configured build. */
function pick(answers: PurposeAnswers, purposes: Purpose[]): Partial<PurposeAnswers> {
  const out: Partial<PurposeAnswers> = {};
  for (const purpose of purposes) out[purpose] = answers[purpose];
  return out;
}
